<template>
  <div>
    <div class="row">
      <div class="col-lg-12">
        <StaticDataTable class="static-table-list">
          <template v-slot:header>
            <tr>
              <th width="25%">Price Type</th>
              <th>Active</th>
              <th v-html="priceHeader"></th>
              <th><Tooltip label="Minimum square metres charged for this stock" helpStyle>Min Size Charge</Tooltip></th>
              <th><Tooltip label="Minimum price per individual item. This amount is not multiplied by sales rates" helpStyle>Min Price Per Item</Tooltip></th>
              <th><Tooltip label="Minimum price per group. This amount is not multiplied by sales rates" helpStyle>Min Price Per Group</Tooltip></th>
              <th><Tooltip label="Minimum invoice charge when this stock item is included. This amount is not multiplied by sales rates" helpStyle>Min Invoice Charge</Tooltip></th>
              <th>Actions</th>
            </tr>
          </template>
          <template v-slot:body>
            <tr v-for="priceGroup in stockPriceGroupList" :key="priceGroup.stockPriceGroupId">
              <td class="font-weight-bold">
                {{priceGroup.name}}
                <span v-if="priceGroup.isPendingUpdate" class="pricing-pending-update">
                  <Tooltip label="Price updates pending"><i class="far fa-clock"></i></Tooltip>
                </span>
              </td>
              <td>
                <FormCheckbox :value="priceGroup.isActive" @change="priceGroup.isActive = $event; priceUpdated()" v-show="editing && !priceGroup.isPendingUpdate" />
                <span v-show="!editing || priceGroup.isPendingUpdate" v-html="dataDisplayTableBoolean(priceGroup.isActive, {showFalse:true})"></span>
              </td>
              <td>
                <FormInputStockPrice :uomType="uomType" :value="priceGroup.price" @input="priceGroup.price = $event; priceUpdated()" v-show="editing && !priceGroup.isPendingUpdate"/>
                <span v-show="!editing || priceGroup.isPendingUpdate">{{priceGroup.price | formatPriceWithUOM(uomType)}}</span>
                <span v-show="editing && priceGroup.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
              </td>
              <td>
                <FormInputText 
                  type="number"
                  addon="m&sup2;"
                  :value="priceGroup.minChargeQuantity"
                  @input="priceGroup.minChargeQuantity = $event; priceUpdated()"
                  v-show="editing && !priceGroup.isPendingUpdate"
                  :placeholder="`${priceGroup.stockPriceGroup.minChargeQuantity}`" />
                <span v-show="(!editing || priceGroup.isPendingUpdate) && isValidNumber(priceGroup.minChargeQuantity)" class="pricing-list-variation" v-html="dataDisplayTableArea(priceGroup.minChargeQuantity)"></span>
                <span v-show="(!editing || priceGroup.isPendingUpdate) && !isValidNumber(priceGroup.minChargeQuantity)" class="pricing-list-inherited" v-html="dataDisplayTableArea(priceGroup.stockPriceGroup.minChargeQuantity)"></span>
                <span v-show="editing && priceGroup.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
              </td>
              <td>
                <FormInputCurrency
                  :value="priceGroup.minChargeItemPrice"
                  @input="priceGroup.minChargeItemPrice = $event; priceUpdated()"
                  v-show="editing && !priceGroup.isPendingUpdate"
                  :placeholder="`${$options.filters.formatCurrency(priceGroup.stockPriceGroup.minChargeItemPrice)}`" />
                <span v-show="(!editing || priceGroup.isPendingUpdate) && isValidNumber(priceGroup.minChargeItemPrice)" class="pricing-list-variation">{{priceGroup.minChargeItemPrice | formatCurrency}}</span>
                <span v-show="(!editing || priceGroup.isPendingUpdate) && !isValidNumber(priceGroup.minChargeItemPrice)" class="pricing-list-inherited">{{priceGroup.stockPriceGroup.minChargeItemPrice | formatCurrency}}</span>
                <span v-show="editing && priceGroup.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
              </td>
              <td>
                <FormInputCurrency
                  :value="priceGroup.minChargePrice"
                  @input="priceGroup.minChargePrice = $event; priceUpdated()"
                  v-show="editing && !priceGroup.isPendingUpdate"
                  :placeholder="`${$options.filters.formatCurrency(priceGroup.stockPriceGroup.minChargePrice)}`" />
                <span v-show="(!editing || priceGroup.isPendingUpdate) && isValidNumber(priceGroup.minChargePrice)" class="pricing-list-variation">{{priceGroup.minChargePrice | formatCurrency}}</span>
                <span v-show="(!editing || priceGroup.isPendingUpdate) && !isValidNumber(priceGroup.minChargePrice)" class="pricing-list-inherited">{{priceGroup.stockPriceGroup.minChargePrice | formatCurrency}}</span>
                <span v-show="editing && priceGroup.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
              </td>
              <td>
                <FormInputCurrency
                  :value="priceGroup.minChargeInvoice"
                  @input="priceGroup.minChargeInvoice = $event; priceUpdated()"
                  v-show="editing && !priceGroup.isPendingUpdate"
                  :placeholder="`${$options.filters.formatCurrency(priceGroup.stockPriceGroup.minChargeInvoice)}`" />
                <span v-show="(!editing || priceGroup.isPendingUpdate) && isValidNumber(priceGroup.minChargeInvoice)" class="pricing-list-variation">{{priceGroup.minChargeInvoice | formatCurrency}}</span>
                <span v-show="(!editing || priceGroup.isPendingUpdate) && !isValidNumber(priceGroup.minChargeInvoice)" class="pricing-list-inherited">{{priceGroup.stockPriceGroup.minChargeInvoice | formatCurrency}}</span>
                <span v-show="editing && priceGroup.isPendingUpdate" class="form-field-note">Price update pending, cannot update</span>
              </td>
              <td>
                <ActionDropMenu :tableAction="true" :forceDropdown="true" v-if="!editing && priceGroup.isPendingUpdate && userHasReadPermission(PermissionTypes.PricingPermissionTypes.pricingApproval)">
                  <DropdownMenuItem :link="`/pricing/requests/${priceGroup.stockPriceRequestGroupId}`" icon="fa-clock" label="Show Pending Prices">Show Pending Prices</DropdownMenuItem>
                </ActionDropMenu>
              </td>
            </tr>
          </template>
        </StaticDataTable>
      </div>
    </div>

    <div class="row mt-2">
      <div class="col-lg-12">
        <div class="btn-group float-right">
          <button class="btn btn-xs btn-white" :class="[isSalesMatrixDisplayPrice ? 'active' : '']" @click.prevent="isSalesMatrixDisplayPrice=true">Price</button>
          <button class="btn btn-xs btn-white" :class="[!isSalesMatrixDisplayPrice ? 'active' : '']" @click.prevent="isSalesMatrixDisplayPrice=false">Diff</button>
        </div>
        <h3>Sales Matrix</h3>
      </div>
    </div>

    <StockPricingMatrix
      :priceCategoryList="stockPriceGroupList"
      :displayPrice="isSalesMatrixDisplayPrice"
      :uomType="uomType"
    />
  </div>
</template>

<script>
import _ from 'lodash'
import * as Types from '@/store/types'
import Utils from '@/utils/utils'
import DataDisplayMixin from '@/mixins/dataDisplay'
import StaticDataTable from "@/components/table/staticDataTable.vue"
import StockPricingMatrix from '@/modules/pricing/components/stockPricingMatrix.vue'
import FormInputStockPrice from '@/modules/pricing/components/formInputStockPrice'


export default {
  name: "SalesGlassPricingEditor",
  mixins: [DataDisplayMixin],
  components: {
    StaticDataTable,
    StockPricingMatrix,
    FormInputStockPrice
  },
  props: {
    priceGroupList: Array,
    uomType: String,
    editing: Boolean
  },
  watch: {
    priceGroupList: function(newVal) { 
      this.$data.stockPriceGroupList = _.cloneDeep(newVal)
    }
  },
  computed: {
    priceHeader() {
      if(this.uomType == Types.UOMTypes.percentOfLineItem || this.uomType == Types.UOMTypes.percentOfOrder)
        return Utils.getLookupName(Types.UOMTypeLookup, this.uomType)

      let uomDisplay = Utils.getLookupName(Types.UOMTypeUnitsLookup, this.uomType)
      return `Price / ${uomDisplay}`
    }
  },
  methods: {
    priceUpdated() {
      this.$emit('update', this.$data.stockPriceGroupList)
    }
  },
  data() {
    return {
      isSalesMatrixDisplayPrice: true,
      stockPriceGroupList: _.cloneDeep(this.$props.priceGroupList)
    }
  },  
}
</script>